import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Camera, ChevronDown, ChevronUp, FileText, Loader2, SlidersHorizontal, TriangleAlert, Upload, X } from 'lucide-react';
import type { Currency, ScanPart, ScanRead } from '@worki/shared';
import { CURRENCIES, roundCurrency } from '@worki/shared';
import { api, RequestError } from '../lib/api.js';
import { blurScore, isBlurry, regionOf } from '../lib/scan/blur.js';
import { clearDraft, DRAFT_KEY, loadDraft, saveDraft } from '../lib/scan/draft.js';
import { fullFrame, scaleQuad, type Quad } from '../lib/scan/geometry.js';
import { readFields } from '../lib/scan/read.js';
import { cropFrom, decodePhoto, findPage, keepPhoto, PAGE_MAX, THUMB_MAX, type Photo } from '../lib/scan/pipeline.js';
import { hasWebgl, renderPage, type ScanFilter } from '../lib/scan/render.js';
import { Button, ErrorNotice, Field, Input } from './ui.js';
import { Modal } from './Modal.js';
import { InfoTip } from './InfoTip.js';
import { CameraView } from './scan/CameraView.js';
import { DropStage } from './scan/DropStage.js';
import { PageEditor } from './scan/PageEditor.js';

/**
 * SCAN — photograph a stack of paper, get one PDF.
 *
 * The shape of this screen is the whole feature. Photographing ten documents is
 * ten shots, not ten rounds of shoot-check-accept-return: the camera stays open,
 * each frame is straightened and cleaned up behind it while the next one is
 * being lined up, and the reviewing happens once, at the end, over all of them
 * at once. A scanner that stops to show you each page is a scanner that takes
 * longer than a photocopier for the one job a photocopier is bad at.
 *
 * So there are two screens and not ten. The camera, which never interrupts, and
 * the review, where the look of the pages is one control for all of them and
 * fixing an individual page's crop is a tap on that page.
 *
 * The pages themselves are in `lib/scan`: finding the paper in the frame
 * (SCAN-002), flattening and cleaning it (SCAN-003), and what is kept in memory
 * in between (SCAN-005).
 */

interface ScanPage {
  /** Stable across reorders, so React keeps the thumbnail it already decoded. */
  id: string;
  /** The photograph, shrunk and compressed. Null for a PDF passed through. */
  photo: Photo | null;
  /** That photograph as a URL, for the editor and its magnifier. */
  url: string | null;
  /** A picked PDF, which is filed as it is. */
  file: File | null;
  /** Corners in the photograph's own pixels. */
  quad: Quad;
  rotation: number;
  /** The rendered preview, or null while it is still being made. */
  thumb: string | null;
  /** Set when rendering the preview failed, so it is not attempted forever. */
  stuck: boolean;
}

const FILTERS: ScanFilter[] = ['colour', 'mono', 'original'];

/**
 * The typed name, made fit to be a file name.
 *
 * It leaves here as the `File`'s name and arrives as a title: the library files
 * the scan under it, the signing screen calls the document by it. So it keeps
 * its Hebrew, its digits and its spaces, and loses only what a path is built
 * out of — and a ".pdf" typed by hand, which would otherwise be doubled.
 */
const fileLabel = (typed: string): string =>
  typed
    .replace(/\.pdf$/i, '')
    .replace(/[\\/:*?"<>|\p{Cc}]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);

/**
 * A file's name without its extension, made fit to be the PDF's name. For a
 * dialog whose pages are files: the first file's name is the best name the scan
 * has, where a photograph from a camera has none.
 */
export const titleOf = (fileName: string): string => fileLabel(fileName.replace(/\.[^./\\]+$/, ''));

/**
 * What the scan can take: a photograph, which is straightened, or a PDF, which
 * is passed through. The picker is told to offer nothing else and a drop is
 * told nothing at all, so this is where a Word file or a folder is turned away,
 * rather than filed as a PDF and refused by the server a page later. Where a
 * document can be filed as it is, nothing is turned away: see `routeFiles`.
 */
export const scannable = (file: File): boolean =>
  file.type.startsWith('image/') || file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

/** A photograph, which is what the scan straightens. Anything else that is chosen is a document. */
const isPhoto = (file: File): boolean => file.type.startsWith('image/');

/**
 * What the picker offers where documents are filed as they are: the list the
 * document library's own picker offers, which is what its upload takes. A test
 * keeps the two the same.
 */
const ACCEPT_AS_IS =
  'application/pdf,.pdf,image/jpeg,image/png,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * 1.58 — where the files that were just taken up go, before any of them is read.
 *
 * Where the caller can file a document as it is (the document library), files
 * picked together are always one document (section 9), and where they go turns
 * on whether a photograph is among them:
 *
 * - With one, the whole batch is scanned, in the order it came: the photograph
 *   gets its crop and its filter (asked for 05.10, over placing it on a sheet as
 *   it is), and a PDF or a Word file beside it rides along as a page that is
 *   passed through. The caller's save (`onSave`) turns the lot into one document.
 * - With none, the batch is handed over whole and the dialog is closed: the
 *   caller files one file as it is, and several as one document after its own
 *   short screen. Nothing handed over is read, merged or run through the PDF
 *   writer here, and nothing is turned away here, because the upload it goes to
 *   reads the bytes and says in its own words what it cannot take.
 *
 * Files added to a scan that is already open are part of that scan, documents
 * included, for the same reason: they were put together.
 *
 * Where the caller cannot, the dialog is what it was: a photograph or a PDF is a
 * page, and anything else is refused.
 */
export function routeFiles(
  files: File[],
  {
    onFileAsIs,
    onDone,
    scanUnderWay,
  }: {
    onFileAsIs?: ((files: File[]) => void) | undefined;
    /** A batch of nothing but documents has been handed over: there is nothing left to do here. */
    onDone: () => void;
    /** Pages are in the scan, or files are still being read into it. */
    scanUnderWay: boolean;
  },
): { scan: File[]; refused: boolean } {
  if (!onFileAsIs) {
    const scan = files.filter(scannable);
    return { scan, refused: scan.length < files.length };
  }
  if (scanUnderWay || files.some(isPhoto)) return { scan: files, refused: false };
  onFileAsIs(files);
  onDone();
  return { scan: [], refused: false };
}

/**
 * 1.46 — a photograph picked from the device, sent the size the camera's own are.
 *
 * What a gallery hands over is what the phone's camera made: twelve megapixels and
 * four or five megabytes a page, which over a mobile connection is most of the time
 * a merge takes. The dialog's own pages never travel like that — they are kept at
 * `keepPhoto`'s size, compressed — so a photograph that reaches the server by some
 * other road is put through the same door first. It comes out the right way up as
 * well: the PDF writer on the other side does not read a JPEG's orientation tag, and
 * the browser applies it when it decodes.
 *
 * Anything that is not a photograph is returned as it is, and so is a photograph the
 * browser cannot open: the server says in its own words what it cannot take, which
 * is better than a failure here with none.
 */
export async function shrinkPicked(file: File): Promise<File> {
  if (!isPhoto(file)) return file;
  try {
    const { blob } = await keepPhoto(await decodePhoto(file));
    // The name it had, which a signing document is called by, and the end it now has.
    return new File([blob], `${file.name.replace(/\.[^./\\]+$/, '')}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

/**
 * 1.46 — how many files the scan holds, in words.
 *
 * The strip under the viewfinder and the review's grid number their thumbnails, and
 * a number on a thumbnail is the last of them, found by looking, not an answer to
 * "how many did I add". Said once, above them, and not at all until there is a file.
 */
export function FilesAdded({ count }: { count: number }) {
  const { t } = useTranslation();
  if (count < 1) return null;
  return (
    <p role="status" className="text-sm font-medium">
      {t('scan.filesAdded', { count })}
    </p>
  );
}

/**
 * 1.46 — how far the PDF has got, beside the button that asked for it.
 *
 * `done` pages are ready of `total`. While there are pages left it is the one it is
 * on, n of N; once there are none it is the file, which has no pages to count — that
 * is the server's part, and for an invoice the reading, which is the long one. A
 * spinner alone said the same for all of it.
 */
export function MakingProgress({ done, total }: { done: number; total: number }) {
  const { t } = useTranslation();
  return (
    <p role="status" className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
      {done < total ? t('scan.making', { current: done + 1, total }) : t('scan.merging')}
    </p>
  );
}

/**
 * 1.11 — what a list of documents adds up to, a currency at a time, shekels first.
 *
 * A shekel and a dollar do not make a figure, and there is no rate here to make
 * one with, so each currency has a sum of its own and none is turned into
 * another. A document whose currency was not read counts as shekels: the answer
 * the server gives when it adds a pile up, and the form's own currency box. Each
 * sum is rounded to the agora, because 10.10 and 20.20 are 30.299999999999997 to a float.
 */
export function totalsByCurrency(parts: readonly ScanPart[]): { currency: Currency; total: number }[] {
  const sums = new Map<Currency, number>();
  for (const part of parts) {
    const currency = part.currency ?? 'ILS';
    sums.set(currency, (sums.get(currency) ?? 0) + part.amount);
  }
  // In the order the product lists its currencies, and not the order the files came in.
  return CURRENCIES.filter((currency) => sums.has(currency)).map((currency) => ({
    currency,
    total: roundCurrency(sums.get(currency) ?? 0, currency),
  }));
}

let pageSeq = 0;

export function QuickScanDialog({
  open,
  onClose,
  onScanned,
  onSave,
  title,
  camera = true,
  onFileAsIs,
  readPages = true,
  onPages,
  checkBlur = false,
  documentMode = false,
  draftKey = DRAFT_KEY,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * The merged PDF, and what its pages turned out to say — nothing, where the
   * caller said it needs no reading. The caller decides where the file goes and
   * whether anything read off it is of use: the invoice screen fills a form in
   * with it, the document library has no form. Not called where `onSave` is given.
   */
  onScanned?: (file: File, pageCount: number, read: ScanRead | null) => void;
  /**
   * Section 9 — the pages, handed to a caller that files them itself as one
   * document, in their order and under the name typed (or "סריקה"), instead of
   * being made a PDF here first. The review is then the caller's pre-save screen:
   * while the promise is out the button spins, and a rejection keeps the dialog
   * open with every page in it, says why, and turns the button into "ניסיון חוזר".
   * The document library gives this; a screen that does not is as it was.
   */
  onSave?: (pages: File[], name: string) => Promise<unknown>;
  title?: string;
  /**
   * 1.46 — whether the server reads what the pages say, which is a second a page
   * of the wait. Only a screen that has a use for the answer wants it, so this is
   * on unless a caller says otherwise: the invoice screen takes it, and the
   * screens that file the PDF and nothing else turn it off.
   */
  readPages?: boolean;
  /**
   * Whether there is a camera to hold up. A desktop has none worth the name, so
   * there the dialog opens on a dropzone in the camera's place: the pages are
   * files, dropped or chosen, and everything after them — the review, the name,
   * the look, the PDF — is the same.
   */
  camera?: boolean;
  /**
   * 1.58 — a way to file a document as it is. Given, a batch with no photograph
   * in it is handed to this whole and untouched, and the dialog closes; a batch
   * with a photograph is scanned, its documents included; and anything added to
   * an open scan joins it (see `routeFiles`). Given together with `onSave`, which is what takes a Word
   * file as a page. Not given, every file is a page, as it was.
   */
  onFileAsIs?: (files: File[]) => void;
  /**
   * Section 3 — the pages handed back as they are, each a file of its own, in
   * their order: a photograph straightened and in its chosen look, a PDF as it
   * was picked. Nothing is merged and nothing is sent from here — the invoices'
   * quick scan reads each page as an invoice of its own. No name is asked for,
   * since there is no one document to name, and the review's button goes on
   * ("המשך") rather than making a PDF. Not given, the dialog is as it was.
   */
  onPages?: (pages: File[]) => void;
  /**
   * Section 3 — check for blur: live in the viewfinder (see `CameraView`), and
   * on every shot, which when blurry asks "לצלם שוב?" before the next one.
   */
  checkBlur?: boolean;
  documentMode?: boolean;
  /** Which unfinished scan this dialog keeps and brings back (see `lib/scan/draft.ts`). */
  draftKey?: string;
}) {
  const { t } = useTranslation();
  const [pages, setPages] = useState<ScanPage[]>([]);
  /** Section 3 — the shot that came out blurry, while "לצלם שוב?" is asked about it. */
  const [blurAsk, setBlurAsk] = useState<string | null>(null);
  /** What the finished PDF is called. Blank is allowed and means "סריקה". */
  const [name, setName] = useState('');
  /** The first stage is where pages are added: the camera, or where there is none the dropzone. */
  const [stage, setStage] = useState<'camera' | 'review'>('camera');
  const [editing, setEditing] = useState<string | null>(null);
  const [filter, setFilter] = useState<ScanFilter>(hasWebgl() ? 'colour' : 'original');
  const [taking, setTaking] = useState(false);
  /** Files that have been taken up and are not pages yet, for the list without a camera. */
  const [reading, setReading] = useState(0);
  /** 1.46 — how many of the pages are made ready for the PDF, while it is being made. */
  const [made, setMade] = useState({ done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const [restored, setRestored] = useState(0);
  const pickRef = useRef<HTMLInputElement>(null);
  /* Read by the restore below, which runs after an await and must not bring a
     scan back on top of one somebody has already started. */
  const pagesRef = useRef<ScanPage[]>([]);
  pagesRef.current = pages;

  const edited = pages.find((page) => page.id === editing) ?? null;
  const pending = pages.filter((page) => page.photo && !page.thumb && !page.stuck).length;

  /*
   * Object URLs are held by the browser until they are released, and a scan
   * holds two or three of them per page. Released when the dialog closes
   * rather than per page, so a thumbnail is never revoked while still on screen.
   */
  useEffect(() => {
    if (open) return;
    setPages((current) => {
      for (const page of current) {
        if (page.url) URL.revokeObjectURL(page.url);
        if (page.thumb) URL.revokeObjectURL(page.thumb);
      }
      return [];
    });
    setStage('camera');
    setEditing(null);
    setFilter('colour');
    setName('');
    setError(null);
    setRestored(0);
    setBlurAsk(null);
  }, [open]);

  /*
   * SCAN-008 — the scan that was open the last time, brought back.
   *
   * Closing this dialog is not throwing the pages away, and neither is the tab
   * being reloaded by a phone that wanted the memory back. Eleven photographs
   * of a contract is a couple of minutes of standing over a desk, and the only
   * two things that end a scan are the file being made and the person saying
   * cancel.
   */
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    void (async () => {
      const draft = await loadDraft(draftKey);
      if (cancelled || !draft || pagesRef.current.length > 0) return;
      setPages(
        draft.pages.map((page) => ({
          ...page,
          // Freshly numbered: the ids in the draft were handed out by a counter
          // that starts again at one when the tab reloads, so keeping them
          // risks two pages of this scan answering to the same name.
          id: `page-${(pageSeq += 1)}`,
          url: page.photo ? URL.createObjectURL(page.photo.blob) : null,
          thumb: null,
          stuck: false,
        })),
      );
      setFilter(draft.filter);
      setName(draft.name ?? '');
      setRestored(draft.pages.length);
      setStage('review');
    })();

    return () => {
      cancelled = true;
    };
  }, [open, draftKey]);

  /*
   * And written down as it changes.
   *
   * After a pause rather than on the keystroke: a burst of photographs would
   * otherwise write the whole stack to disk once per photograph, each write
   * carrying every page taken so far.
   */
  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(() => {
      void saveDraft(
        pages.map(({ id, photo, file, quad, rotation }) => ({ id, photo, file, quad, rotation })),
        filter,
        name,
        draftKey,
      );
    }, 400);
    return () => clearTimeout(handle);
  }, [open, pages, filter, name, draftKey]);

  /*
   * One page at a time, whichever is still waiting for a preview.
   *
   * Written as "find the next unfinished one" rather than as a queue because
   * this has to be right under a camera that keeps adding to it, a filter that
   * invalidates all of it at once, and a person deleting pages out of the
   * middle while it runs. A queue would need every one of those to remember to
   * update it; a search over the current state cannot fall out of step with
   * the current state.
   */
  useEffect(() => {
    const next = pages.find((page) => page.photo && !page.thumb && !page.stuck);
    const photo = next?.photo;
    if (!next || !photo) return;

    let cancelled = false;
    void (async () => {
      try {
        const source = await decodePhoto(photo.blob);
        const { blob } = await renderPage(source, {
          quad: next.quad,
          filter,
          rotation: next.rotation,
          maxSize: THUMB_MAX,
        });
        if (source instanceof ImageBitmap) source.close();
        const url = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setPages((current) =>
          current.map((page) => (page.id === next.id ? { ...page, thumb: url } : page)),
        );
      } catch {
        if (cancelled) return;
        // Marked rather than retried: the same page would fail the same way,
        // and the loop would spin. It still goes into the PDF — a page with no
        // preview is a page you cannot check, not a page you have lost.
        setPages((current) =>
          current.map((page) => (page.id === next.id ? { ...page, stuck: true } : page)),
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pages, filter]);

  /** Choosing how the pages look, for every page at once. */
  const choose = (next: ScanFilter) => {
    if (next === filter) return;
    setFilter(next);
    setPages((current) =>
      current.map((page) => {
        if (page.thumb) URL.revokeObjectURL(page.thumb);
        return { ...page, thumb: null, stuck: false };
      }),
    );
  };

  const add = (page: Omit<ScanPage, 'id' | 'thumb' | 'stuck'>): string => {
    const id = `page-${(pageSeq += 1)}`;
    setRestored(0);
    setPages((current) => [...current, { ...page, id, thumb: null, stuck: false }]);
    return id;
  };

  /** A frame off the camera, with wherever the viewfinder had found the page. */
  const capture = async (frame: ImageBitmap, live: Quad | null) => {
    const sourceWidth = frame.width;
    const sourceHeight = frame.height;
    setTaking(true);
    try {
      // Section 3 — measured on the frame as it came, the page where one was
      // found, before it is shrunk and put away (which lets go of the frame).
      const score = checkBlur ? await blurScore(frame, regionOf(live, sourceWidth, sourceHeight)) : null;
      const photo = await keepPhoto(frame);
      let quad: Quad;
      if (!documentMode) {
        quad = fullFrame(photo.width, photo.height);
      } else if (live) {
        // The outline the person actually saw around the page, measured against
        // the frame at its own size and now against the shrunk copy of it.
        quad = cropFrom(scaleQuad(live, photo.width / sourceWidth, photo.height / sourceHeight));
      } else {
        const decoded = await decodePhoto(photo.blob);
        quad = findPage(decoded);
        if (decoded instanceof ImageBitmap) decoded.close();
      }
      const id = add({ photo, url: URL.createObjectURL(photo.blob), file: null, quad, rotation: 0 });
      if (score !== null && isBlurry(score)) setBlurAsk(id);
    } catch {
      setError('errors.unknown');
    } finally {
      setTaking(false);
    }
  };

  /**
   * Files chosen from the device, or dropped on it: photographs are scanned and
   * PDFs are passed through. Where documents are filed as they are, a batch with
   * one in it was handed over before anything was read, unless a scan is open,
   * which it joins (see `routeFiles`).
   */
  const addFiles = async (list: FileList | File[] | null) => {
    if (!list?.length) return;
    setError(null);
    const { scan, refused } = routeFiles([...list], {
      onFileAsIs,
      onDone: onClose,
      scanUnderWay: pages.length > 0 || reading > 0,
    });
    // The picker was told what to offer and a drop was told nothing, so a file
    // that is neither a photograph nor a PDF is said to be refused, not filed.
    if (refused) setError('validation.scan.fileType');
    const [first] = scan;
    if (!first) return;
    // Without a camera the pages are files, and a file has a name: the PDF is
    // called by the first one's unless a name was already typed. Left to the
    // default it would be "סריקה", which is not what uploading "חוזה.pdf" filed.
    // A batch with a photograph in it is scanned whole, so the first page is the
    // first file picked, a document or not.
    if (!camera) setName((typed) => typed || titleOf(first.name));
    setReading((count) => count + scan.length);

    for (const file of scan) {
      try {
        if (!isPhoto(file)) {
          add({ photo: null, url: null, file, quad: fullFrame(1, 1), rotation: 0 });
          continue;
        }
        const decoded = await decodePhoto(file);
        const photo = await keepPhoto(decoded);
        const reopened = await decodePhoto(photo.blob);
        const quad = findPage(reopened);
        if (reopened instanceof ImageBitmap) reopened.close();
        add({ photo, url: URL.createObjectURL(photo.blob), file: null, quad, rotation: 0 });
      } catch {
        setError('errors.unknown');
      } finally {
        setReading((count) => count - 1);
      }
    }
    setStage('review');
  };

  const remove = (id: string) => {
    setBlurAsk((asked) => (asked === id ? null : asked));
    setPages((current) => {
      const page = current.find((entry) => entry.id === id);
      if (page?.url) URL.revokeObjectURL(page.url);
      if (page?.thumb) URL.revokeObjectURL(page.thumb);
      return current.filter((entry) => entry.id !== id);
    });
  };

  /** Cancel, and only cancel, throws the scan away — the ✕ and Escape are the
   *  ways out that a person takes by accident, and they keep it. */
  const discard = () => {
    void clearDraft(draftKey);
    onClose();
  };

  const move = (index: number, by: -1 | 1) =>
    setPages((current) => {
      const target = index + by;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(index, 1);
      if (moved) next.splice(target, 0, moved);
      return next;
    });

  const merge = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      // 1.46 — said only when the answer is not wanted. A server that is told
      // nothing reads the pages, which is what the invoice screen needs and what
      // every installed copy of the app expects.
      if (!readPages) form.append('read', 'false');
      // Rendered here at full size rather than at the size of the thumbnail
      // beside it: the review grid is checking the crop and the filter, and
      // five hundred pixels is enough to check those and not nearly enough to
      // read the page afterwards.
      for (const [index, page] of pages.entries()) {
        setMade({ done: index, total: pages.length });
        if (page.file) {
          form.append('pages', page.file, page.file.name || `page-${index + 1}`);
          continue;
        }
        if (!page.photo) continue;
        const source = await decodePhoto(page.photo.blob);
        const { blob } = await renderPage(source, {
          quad: page.quad,
          filter,
          rotation: page.rotation,
          maxSize: PAGE_MAX,
        });
        if (source instanceof ImageBitmap) source.close();
        const suffix = blob.type === 'image/png' ? 'png' : 'jpg';
        form.append('pages', blob, `page-${index + 1}.${suffix}`);
      }
      // Every page is ready: what is left is the server's, and has no pages to count.
      setMade({ done: pages.length, total: pages.length });

      /*
       * Section 3 — a caller that reads every page as an invoice of its own gets
       * them as they are, one file a page, and does the rest itself.
       */
      if (onPages) {
        const parts = form.getAll('pages').filter((part): part is File => part instanceof File);
        onPages(parts);
        return { file: null, pageCount: parts.length, read: null };
      }

      /*
       * Section 9 — a caller that files the pages itself gets them as they are, in
       * their order and under the name typed, and makes them one document in one
       * request. Nothing is made here first, so a refusal there leaves every page
       * here, and the draft with them, to be sent again.
       */
      if (onSave) {
        const parts = form.getAll('pages').filter((part): part is File => part instanceof File);
        await onSave(parts, fileLabel(name) || t('scan.fileName'));
        return { file: null, pageCount: parts.length, read: null };
      }

      const { blob, headers } = await api.uploadBlob('/scan/pdf', form);
      const count = Number(headers.get('X-Page-Count') ?? pages.length);
      return {
        file: new File([blob], `${fileLabel(name) || t('scan.fileName')}.pdf`, { type: 'application/pdf' }),
        pageCount: Number.isFinite(count) ? count : pages.length,
        read: readFields(headers.get('X-Scan-Read')),
      };
    },
    onSuccess: ({ file, pageCount, read }) => {
      void clearDraft(draftKey);
      if (file) onScanned?.(file, pageCount, read);
      onClose();
    },
    onError: (cause) => {
      const failure = cause instanceof RequestError ? cause : null;
      setError(failure?.fields?.files ?? failure?.code ?? 'errors.unknown');
    },
  });

  const footer = edited ? null : stage === 'camera' ? (
    <>
      <Button type="button" variant="ghost" onClick={discard}>
        {t('common.cancel')}
      </Button>
      <Button
        type="button"
        variant="primary"
        disabled={pages.length === 0}
        onClick={() => setStage('review')}
      >
        {t('scan.done')}
      </Button>
    </>
  ) : (
    <>
      {/* 1.46 — where the PDF has got to, in words and not only in the spinner. */}
      {merge.isPending && <MakingProgress done={made.done} total={made.total} />}
      <Button
        type="button"
        variant="ghost"
        icon={camera ? <Camera className="h-4 w-4" aria-hidden /> : <Upload className="h-4 w-4" aria-hidden />}
        onClick={() => setStage('camera')}
      >
        {t(camera ? 'scan.addMore' : 'scan.addMoreFiles')}
      </Button>
      <Button
        type="button"
        variant="primary"
        disabled={pages.length === 0 || pending > 0}
        loading={merge.isPending}
        onClick={() => {
          // Set before the merge starts, so that the first thing it says is page 1 of
          // them and not the last thing the one before it said.
          setMade({ done: 0, total: pages.length });
          merge.mutate();
        }}
      >
        {/* Section 9 — where the caller files the pages, a refusal leaves them all
            here, and the next press is a retry of the same save. Section 3 —
            where the caller reads them, this hands them over and goes on. */}
        {onPages ? t('invoiceScan.next') : <>{onSave && merge.isError ? t('common.retry') : t('scan.create')}</>}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      title={edited ? t('scan.editTitle') : title ?? t('scan.title')}
      onClose={onClose}
      size="wide"
      /* One page, one way out. The ✕ in the corner closes the whole scan, which
         beside a page being looked at reads as "close this page" — and the two
         buttons under it already say what leaving does. */
      hideClose={Boolean(edited)}
      {...(footer ? { footer } : {})}
    >
      <div className="space-y-3">
        {error && <ErrorNotice code={error} />}

        {restored > 0 && !edited && (
          <p className="text-xs rounded-lg bg-muted px-3 py-2 text-muted-foreground">
            {t('scan.restored', { count: restored })}
          </p>
        )}

        <input
          ref={pickRef}
          type="file"
          accept={onFileAsIs ? ACCEPT_AS_IS : 'image/jpeg,image/png,application/pdf'}
          multiple
          className="sr-only"
          onChange={(event) => {
            void addFiles(event.target.files);
            event.target.value = '';
          }}
        />

        {edited && edited.photo && edited.url ? (
          <PageEditor
            url={edited.url}
            photo={edited.photo}
            quad={edited.quad}
            rotation={edited.rotation}
            filter={filter}
            onCancel={() => setEditing(null)}
            onApply={({ quad, rotation }) => {
              setPages((current) =>
                current.map((page) => {
                  if (page.id !== edited.id) return page;
                  if (page.thumb) URL.revokeObjectURL(page.thumb);
                  return { ...page, quad, rotation, thumb: null, stuck: false };
                }),
              );
              setEditing(null);
            }}
          />
        ) : stage === 'camera' && !camera ? (
          <DropStage
            pages={pages}
            reading={reading}
            filesAsIs={Boolean(onFileAsIs)}
            onPick={() => pickRef.current?.click()}
            onFiles={(files) => void addFiles(files)}
            onRemove={remove}
          />
        ) : stage === 'camera' ? (
          <>
            {/* Section 3 — asked of a blurry shot before the next one is taken,
                above the viewfinder so it is seen without scrolling. The shutter
                waits for the answer, or it would take the next page meanwhile. */}
            {blurAsk && (
              <div
                role="alert"
                className="flex flex-wrap items-center gap-2 rounded-xl border border-warning/30 bg-warning/10 px-3 py-2"
              >
                <span className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium text-warning">
                  <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden />
                  {t('invoiceScan.blur.ask')}
                </span>
                <span className="flex shrink-0 gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    icon={<Camera className="h-3.5 w-3.5" aria-hidden />}
                    onClick={() => remove(blurAsk)}
                  >
                    {t('invoiceScan.blur.retake')}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setBlurAsk(null)}>
                    {t('invoiceScan.blur.keep')}
                  </Button>
                </span>
              </div>
            )}

            <CameraView
              onCapture={capture}
              onPickFiles={() => pickRef.current?.click()}
              disabled={taking || blurAsk !== null}
              checkBlur={checkBlur}
              documentMode={documentMode}
            />

            <FilesAdded count={pages.length + reading} />

            {/* What has been taken so far, newest last, so the count and the
                order are both visible without leaving the camera. */}
            {pages.length > 0 && (
              <ul className="flex gap-2 overflow-x-auto pb-1">
                {pages.map((page, index) => (
                  <li
                    key={page.id}
                    className="relative shrink-0 w-14 h-[4.6rem] rounded-md border border-border bg-muted overflow-hidden"
                  >
                    {page.thumb ? (
                      <img src={page.thumb} alt="" className="w-full h-full object-contain" />
                    ) : (
                      <span className="w-full h-full flex items-center justify-center text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      </span>
                    )}
                    {/* A bad shot is noticed while the next page is being lined
                        up, and until now the only way to drop it was to finish
                        the scan first and delete it from the review grid. */}
                    <button
                      type="button"
                      onClick={() => remove(page.id)}
                      aria-label={t('scan.removePage', { index: index + 1 })}
                      className="absolute top-0 end-0 p-1 rounded-es-md bg-background/85 text-destructive hover:bg-destructive/10"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    <span className="absolute bottom-0 inset-x-0 text-center text-[10px] bg-background/85 ltr-nums">
                      {index + 1}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <p className="text-xs text-muted-foreground">{t('scan.hint')}</p>
          </>
        ) : (
          <>
            <FilesAdded count={pages.length + reading} />

            {/* Asked here and not at the camera, because here is where the
                thing being named exists to be looked at — and asked at all
                because the name is what the scan is filed and found under
                afterwards, and every scan Worki has ever made is called
                "סריקה". Left blank it still is: naming a photograph of a
                delivery note is worth offering and not worth insisting on. */}
            {/* Section 3 — pages read as invoices of their own are no one
                document, and have nothing to be called. */}
            {!onPages && (
              <Field label={t('scan.name')} htmlFor="scan-name" hint={t('scan.nameHint')}>
                <Input
                  id="scan-name"
                  value={name}
                  maxLength={120}
                  placeholder={t('scan.fileName')}
                  onChange={(event) => setName(event.target.value)}
                />
              </Field>
            )}

            {/* One control for every page. Choosing a look per page is a choice
                nobody wants to make twenty times, and a scan whose pages do not
                match reads as twenty documents rather than one. */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <SlidersHorizontal className="h-4 w-4" aria-hidden />
                {t('scan.filter.label')}
                <InfoTip label={t('common.explain')}>{t('scan.filter.info')}</InfoTip>
              </span>
              {FILTERS.map((name) => {
                const gl = hasWebgl();
                const disabled = !gl && name !== 'original';
                return (
                <button
                  key={name}
                  type="button"
                  onClick={() => !disabled && choose(name)}
                  disabled={disabled}
                  aria-pressed={filter === name}
                  title={disabled ? t('scan.filter.original') : undefined}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    disabled
                      ? 'border-border text-muted-foreground/40 cursor-not-allowed'
                      : filter === name
                        ? 'border-primary bg-primary/10 text-primary font-medium'
                        : 'border-border text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {t(`scan.filter.${name}`)}
                </button>
                );
              })}
              {pending > 0 && (
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground ms-auto">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  {t('scan.applying', { count: pending })}
                </span>
              )}
            </div>

            <p className="text-xs text-muted-foreground">{t('scan.reviewHint')}</p>

            <ul className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {pages.map((page, index) => (
                <li
                  key={page.id}
                  className="relative rounded-lg border border-border bg-muted overflow-hidden aspect-[3/4]"
                >
                  {page.file ? (
                    <span className="w-full h-full flex flex-col items-center justify-center gap-1 text-muted-foreground">
                      <FileText className="h-6 w-6" aria-hidden />
                      <span className="text-[10px] px-1 truncate max-w-full">{page.file.name}</span>
                    </span>
                  ) : page.thumb ? (
                    // The whole tile opens the editor. A separate "edit" button
                    // would be a fifth control on a thumbnail that already has
                    // four, at a size where they are already close together.
                    <button
                      type="button"
                      onClick={() => setEditing(page.id)}
                      aria-label={t('scan.editPage', { index: index + 1 })}
                      className="w-full h-full"
                    >
                      <img src={page.thumb} alt="" className="w-full h-full object-contain" />
                    </button>
                  ) : (
                    <span className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                    </span>
                  )}

                  {/* The number is the point of the grid: it is what says the
                      pages are in an order, and which order they are in. */}
                  <span className="absolute top-1 start-1 px-1.5 rounded-md bg-background/85 text-[11px] font-semibold tabular-nums">
                    {index + 1}
                  </span>

                  <button
                    type="button"
                    onClick={() => remove(page.id)}
                    aria-label={t('scan.removePage', { index: index + 1 })}
                    className="absolute top-1 end-1 p-1 rounded-md bg-background/85 text-destructive hover:bg-destructive/10"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>

                  <span className="absolute bottom-1 start-1 end-1 flex justify-between">
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                      aria-label={t('scan.movePageUp', { index: index + 1 })}
                      className="p-1 rounded-md bg-background/85 disabled:opacity-30 hover:bg-accent"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      disabled={index === pages.length - 1}
                      onClick={() => move(index, 1)}
                      aria-label={t('scan.movePageDown', { index: index + 1 })}
                      className="p-1 rounded-md bg-background/85 disabled:opacity-30 hover:bg-accent"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Modal>
  );
}
